import type { ReleasedFix, ReleasedSuggestion } from '@epc/contracts';
import { moduleForPath, visibleModules } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { markReleasesSeen, matchesQuery, searchReleases, visibleReleases } from '../lib/whatsnew';
import { EmptyState } from '../ui/feedback';
import { Field, Input } from '../ui/form';
import { Page, PageHeader } from '../ui/layout';

/** Время выкладки по часам читателя; пока оно не наступило (запись готовится к выкладке) — только дата */
const when = (iso: string) => {
  const d = new Date(iso);
  if (d.getTime() > Date.now()) return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};

/** «Что нового» (БЗ-04): записи о выкладках по разделам, открытым человеку */
export function WhatsNewPage() {
  const me = useMe();
  const [query, setQuery] = useState('');
  const visible = useMemo(() => visibleReleases(me.access), [me.access]);
  const found = useMemo(() => searchReleases(visible, query), [visible, query]);
  // Ссылка «Где посмотреть» — только в раздел, открытый человеку
  const openCodes = useMemo(() => new Set(visibleModules(me.access).map((m) => m.code)), [me.access]);
  const linkOpen = (link: string) => {
    const m = moduleForPath(link);
    return !m || openCodes.has(m.code);
  };

  // Выпущенные предложения сотрудников: что и по чьей просьбе сделано (БЗ-30)
  const released = useQuery({
    queryKey: ['support', 'released'],
    queryFn: () => api<ReleasedSuggestion[]>('/support/suggestions/released'),
    enabled: !!me.tenant?.staff,
    retry: false,
  });

  // Исправленные ошибки по обращениям в поддержку (БЗ-25)
  const fixes = useQuery({
    queryKey: ['support', 'fixes'],
    queryFn: () => api<ReleasedFix[]>('/support/fixes/released'),
    enabled: !!me.tenant?.staff,
    retry: false,
  });

  // Поиск идёт и по выпущенным предложениям и исправлениям — иначе ниже «Ничего не нашлось» остаётся весь список
  const releasedFound = (released.data ?? []).filter((s) =>
    matchesQuery(`${s.number} ${s.title} ${s.note ?? ''}`, query),
  );
  const fixesFound = (fixes.data ?? []).filter((f) =>
    matchesQuery(`${f.number} ${f.subject} ${f.note}`, query),
  );

  useEffect(() => {
    markReleasesSeen();
  }, []);

  return (
    <Page>
      <PageHeader title="Что нового" subtitle="Что изменилось в портале после каждой выкладки" />
      <div className="mb-4 max-w-3xl">
        <Field label="Поиск по записям" htmlFor="whats-new-search">
          <Input
            id="whats-new-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Например: табель, наряд-допуск, письма"
            className="min-h-11 md:min-h-0"
          />
        </Field>
      </div>
      {query.trim() && !found.length && !releasedFound.length && !fixesFound.length ? (
        <div className="max-w-3xl">
          <EmptyState title="Ничего не нашлось">
            Попробуйте другое слово. Ищем в заголовках, пунктах и пояснениях, зачем это сделано, — только по
            разделам, открытым вам.
          </EmptyState>
        </div>
      ) : null}
      <ol className="flex max-w-3xl flex-col gap-4">
        {found.map((r) => (
          <li key={r.id} className="rounded-lg border border-line bg-surface px-5 py-4">
            <time dateTime={r.at} className="num text-[13px] text-ink-2">
              {when(r.at)}
            </time>
            <h2 className="mt-0.5 text-[16px] leading-6 font-semibold text-ink">{r.title}</h2>
            <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[14px] leading-6 text-ink">
              {r.items.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            {r.why ? <p className="mt-2 text-[14px] leading-6 text-ink-2">Зачем: {r.why}</p> : null}
            {r.link && linkOpen(r.link) ? (
              <Link
                to={r.link.split('?')[0] ?? r.link}
                search={Object.fromEntries(new URLSearchParams(r.link.split('?')[1] ?? '')) as never}
                className="mt-1 inline-flex min-h-11 items-center text-[14px] font-medium text-accent-text hover:underline md:min-h-0"
              >
                Где посмотреть
              </Link>
            ) : null}
          </li>
        ))}
      </ol>
      {releasedFound.length ? (
        <section className="mt-8 max-w-3xl">
          <h2 className="text-[18px] leading-7 font-semibold text-ink">Сделано по вашим предложениям</h2>
          <ul className="mt-3 flex flex-col gap-3">
            {releasedFound.map((s) => (
              <li key={s.id} className="rounded-lg border border-line bg-surface px-5 py-4">
                <time dateTime={s.releasedAt} className="num text-[13px] text-ink-2">
                  {when(s.releasedAt)}
                </time>
                <h3 className="mt-0.5 text-[15px] leading-6 font-semibold text-ink">
                  <Link
                    to="/support/suggestions/$id"
                    params={{ id: s.id }}
                    className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                  >
                    {s.number} {s.title}
                  </Link>
                </h3>
                {s.note ? <p className="mt-1 text-[14px] leading-6 text-ink">{s.note}</p> : null}
                {s.authorName ? (
                  <p className="mt-1 text-[13px] text-ink-2">Предложил: {s.authorName}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {fixesFound.length ? (
        <section className="mt-8 max-w-3xl">
          <h2 className="text-[18px] leading-7 font-semibold text-ink">
            Исправлено по обращениям в поддержку
          </h2>
          <ul className="mt-3 flex flex-col gap-3">
            {fixesFound.map((f) => (
              <li key={f.id} className="rounded-lg border border-line bg-surface px-5 py-4">
                <time dateTime={f.releasedAt} className="num text-[13px] text-ink-2">
                  {when(f.releasedAt)}
                </time>
                <h3 className="mt-0.5 text-[15px] leading-6 font-semibold text-ink">
                  {f.canOpen ? (
                    <Link
                      to="/support/tickets/$id"
                      params={{ id: f.id }}
                      className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                    >
                      {f.number} {f.subject}
                    </Link>
                  ) : (
                    `${f.number} ${f.subject}`
                  )}
                </h3>
                <p className="mt-1 text-[14px] leading-6 text-ink">{f.note}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Page>
  );
}
